import { useLocation } from "react-router-dom";
import Link from "@/components/LocalizedLink";
import { ArrowRight, ArrowUp, ExternalLink } from "lucide-react";
import {
  CONTACT_EMAIL,
  LINKEDIN_URL,
  detailedIndustryGuides,
  serviceCatalogue,
  getCtaLabel,
  localize,
  siteContent,
} from "@/content/site";
import { useTranslation } from "@/i18n/useTranslation";
import PrimaryCtaLink from "@/components/PrimaryCtaLink";
import VertLogo from "@/components/VertLogo";
import { stripLocalePrefix } from "@/i18n/localePath";
import { utilityRouteCopy } from "@/content/utilityRouteCopy";

type FooterLink = {
  href: string;
  label: string;
  external?: boolean;
};

const isActivePath = (pathname: string, href: string) => (
  href === "/" ? pathname === "/" : pathname === href || pathname.startsWith(`${href}/`)
);

const Footer = () => {
  const { t, locale } = useTranslation();
  const location = useLocation();
  const activePathname = stripLocalePrefix(location.pathname);
  const copy = utilityRouteCopy.footer[locale];
  const primaryCtaLabel = getCtaLabel(siteContent.ctas.primary, locale, "shortLabel");
  const positioning = localize(siteContent.brand.shortPositioning, locale);
  const serviceLinks: FooterLink[] = serviceCatalogue.map((service) => ({
    href: service.href,
    label: localize(service.name, locale),
  }));
  const industryLinks: FooterLink[] = [
    { href: "/industries", label: copy.industriesOverview },
    ...detailedIndustryGuides.map((industry) => ({
      href: industry.href,
      label: localize(industry.label, locale),
    })),
    { href: "/industries#additional-contexts", label: copy.beyondCommerce },
  ];
  const companyLinks: FooterLink[] = [
    { href: "/work", label: t("nav.work") },
    { href: "/about", label: t("nav.about") },
    { href: "/solutions", label: copy.solutions },
    { href: "/contact", label: copy.primaryCta },
    { href: "/privacy", label: copy.privacy },
  ];
  const insightLinks: FooterLink[] = [
    { href: "/insights", label: t("nav.insights") },
    { href: "/insights/vet-ai-vendor", label: copy.vetVendor },
    { href: "/insights/production-grade-ai", label: copy.productionAi },
    { href: "/insights/workflows-deserve-ai", label: copy.workflows },
  ];

  const scrollToTop = () => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
  };

  const renderLinks = (links: FooterLink[]) => (
    <ul className="space-y-2.5 text-[13px] leading-snug text-content-on-dark sm:space-y-3.5">
      {links.map((link) => (
        <li key={`${link.href}-${link.label}`}>
          {link.external ? (
            <a
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="focus-ring inline-flex min-h-9 items-center gap-1.5 py-1 transition-colors duration-400 hover:text-[hsl(var(--sd-fg)/0.92)] sm:min-h-0 sm:py-0"
            >
              {link.label}
              <ExternalLink className="h-3 w-3" aria-hidden="true" />
            </a>
          ) : (
            <Link
              to={link.href}
              aria-current={isActivePath(activePathname, link.href) ? "page" : undefined}
              className="focus-ring inline-flex min-h-9 items-center py-1 transition-colors duration-400 hover:text-[hsl(var(--sd-fg)/0.92)] aria-[current=page]:text-[hsl(var(--sd-fg))] sm:min-h-0 sm:py-0"
            >
              {link.label}
            </Link>
          )}
        </li>
      ))}
    </ul>
  );

  return (
    <footer className="section-dark grain relative" aria-labelledby="site-footer-heading">
      <div className="shimmer-line" />

      <div className="container-wide relative z-10 pb-12 pt-20 md:pb-14 md:pt-28">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-[1.05fr_2fr] lg:gap-20">
          <div className="max-w-md">
            <Link to="/" className="focus-ring group mb-6 inline-flex items-center" aria-label={copy.homeAria}>
              <VertLogo className="h-5 w-auto text-[hsl(var(--sd-fg))] transition-opacity duration-300 group-hover:opacity-60" />
              <span className="sr-only">Vert AI</span>
            </Link>
            <h2 id="site-footer-heading" className="sr-only">
              {copy.navAria}
            </h2>
            <p className="max-w-[28rem] text-[15px] leading-[1.8] text-content-on-dark">
              {positioning}
            </p>
            <PrimaryCtaLink
              cta={siteContent.ctas.primary}
              labelVariant="shortLabel"
              dataEventLocation="footer"
              dataEventSurface="site_footer"
              className="focus-ring mt-8 inline-flex items-center gap-2 border border-[hsl(var(--sd-fg)/0.22)] px-5 py-3 font-mono text-[10px] font-medium uppercase tracking-[0.1em] text-[hsl(var(--sd-fg))] transition-all duration-500 hover:border-primary/55 hover:text-primary"
            >
              {primaryCtaLabel}
              <ArrowRight className="h-3 w-3" aria-hidden="true" />
            </PrimaryCtaLink>
            <div className="mt-9 space-y-2 text-[13px] leading-[1.7] text-content-on-dark">
              <Link
                to="/contact"
                aria-current={isActivePath(activePathname, "/contact") ? "page" : undefined}
                className="focus-ring flex min-h-9 items-center transition-colors duration-400 hover:text-[hsl(var(--sd-fg)/0.92)] aria-[current=page]:text-[hsl(var(--sd-fg))] sm:min-h-0"
              >
                {copy.contact}
              </Link>
              <a href={`mailto:${CONTACT_EMAIL}`} className="focus-ring flex min-h-9 items-center break-all transition-colors duration-400 hover:text-[hsl(var(--sd-fg)/0.92)] sm:min-h-0">
                {CONTACT_EMAIL}
              </a>
              <p>{t("contact.location")}</p>
            </div>
          </div>

          <nav
            className="grid grid-cols-1 gap-10 sm:grid-cols-2 xl:grid-cols-4"
            aria-label={copy.linksAria}
          >
            <div>
              <h3 className="mb-6 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--sd-fg))]">
                {t("footer.services")}
              </h3>
              {renderLinks(serviceLinks)}
            </div>

            <div>
              <h3 className="mb-6 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--sd-fg))]">
                {t("footer.industries")}
              </h3>
              {renderLinks(industryLinks)}
            </div>

            <div>
              <h3 className="mb-6 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--sd-fg))]">
                {t("footer.company")}
              </h3>
              {renderLinks([
                ...companyLinks,
                ...(LINKEDIN_URL ? [{ href: LINKEDIN_URL, label: "LinkedIn", external: true }] : []),
              ])}
            </div>

            <div>
              <h3 className="mb-6 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--sd-fg))]">
                {t("footer.insights")}
              </h3>
              {renderLinks(insightLinks)}
            </div>
          </nav>
        </div>
      </div>

      <div className="container-wide relative z-10 pb-8">
        <div className="mb-7 h-px bg-[hsl(var(--sd-fg)/0.04)]" />
        <div className="flex flex-col items-start justify-between gap-5 text-[12px] text-content-on-dark sm:flex-row sm:items-center">
          <p>© {new Date().getFullYear()} {t("footer.copyright")}</p>
          <div className="flex items-center gap-6">
            <p className="font-mono text-[10px] tracking-[0.12em]">{t("footer.bottomTagline")}</p>
            <button
              onClick={scrollToTop}
              className="focus-ring flex h-11 w-11 items-center justify-center rounded-sm border border-[hsl(var(--sd-fg)/0.24)] text-content-on-dark transition-all duration-500 hover:border-primary/45 hover:text-[hsl(var(--sd-fg))] hover:shadow-[0_0_12px_hsl(var(--primary)/0.08)]"
              aria-label={copy.backToTop}
            >
              <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
